import { DAPStatusEnum } from '@ytl/common-model';
import { borgAnalytics, Button, getChannel, useTheme } from '@ytl/common-web';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as ErrorIcon } from '../../../../assets/error.svg';
import { useLanguage } from '../../../../contexts/languageContext';
import { OnboardingStepperControls } from '../../../../modules/onboarding/OnboardingComponent';
import { getConfig } from '../../../data/config';

type ErrorPageProps = {
  stepper?: OnboardingStepperControls;
  errorCode?: string;
  initiatedFrom?: string;
  retryEvent?: () => void;
  hideIcon?: boolean;
};

export const ErrorPage = ({ stepper, errorCode, hideIcon = false, initiatedFrom = 'ONBOARDING' }: ErrorPageProps) => {
  const { translation } = useLanguage();
  const { isWebView } = useTheme();
  const navigate = useNavigate();

  // Get error message based on error code
  const getErrorMessage = () => {
    switch (errorCode) {
      case DAPStatusEnum.SECOND_CONSENT_REQUIRED:
        borgAnalytics.sendVirtualPageView({
          channel: getChannel(isWebView),
          virtualName: 'Dap error process',
        });
        return {
          title: translation.onboarding.error.queryError.title,
          description: translation.onboarding.error.queryError.description,
        };

      case DAPStatusEnum.DAP_CONSENT_IN_PROGRESS:
        borgAnalytics.sendVirtualPageView({
          channel: getChannel(isWebView),
          virtualName: 'Dap error rejected',
        });
        return {
          title: translation.onboarding.error.permissionError.title,
          description: translation.onboarding.error.permissionError.description,
        };

      case DAPStatusEnum.NO_SUBSCRIBER_FOUND:
        borgAnalytics.sendVirtualPageView({
          channel: getChannel(isWebView),
          virtualName: 'Dap error no ca found',
        });
        return {
          title: translation.onboarding.error.nosubError.title,
          description: translation.onboarding.error.nosubError.description,
        };

      case DAPStatusEnum.UNEXPECTED_ERROR:
        borgAnalytics.sendVirtualPageView({
          channel: getChannel(isWebView),
          virtualName: 'Dap error technical',
        });
        return {
          title: translation.onboarding.error.unexpectedError.title,
          description: translation.onboarding.error.unexpectedError.description,
        };

      case 'DAP_ERROR':
        borgAnalytics.sendVirtualPageView({
          channel: getChannel(isWebView),
          virtualName: 'Dap error technical',
        });
        return {
          title: translation.onboarding.error.dapError.title,
          description: translation.onboarding.error.dapError.description,
        };

      case 'GENERAL_ERROR':
        borgAnalytics.sendVirtualPageView({
          channel: getChannel(isWebView),
          virtualName: 'Error technical',
        });
        return {
          title: translation.onboarding.error.generalError.title,
          description: translation.onboarding.error.generalError.description,
        };

      case 'KYC_ERROR':
        return {
          title: translation.onboarding.kyc.errorTitle,
          description: translation.onboarding.kyc.errorText,
        };

      default:
        borgAnalytics.sendVirtualPageView({
          channel: getChannel(isWebView),
          virtualName: 'Dap error technical',
        });
        return {
          title: translation.onboarding.error.unexpectedError.title,
          description: translation.onboarding.error.unexpectedError.description,
        };
    }
  };
  const { title, description } = getErrorMessage();

  const handleBack = useCallback(() => {
    if (isWebView) {
      window.location.href = getConfig().closeWebViewUrl;
    } else {
      window.location.href = getConfig().mainYettelUrl;
    }
  }, [isWebView]);

  const handleRetry = useCallback(() => {
    if (isWebView) {
      if (initiatedFrom === 'KYC') {
        navigate('/onboarding?app=true');
      } else {
        window.location.reload();
      }
      return;
    }
    stepper?.resetOnboarding();
  }, [stepper, isWebView, initiatedFrom, navigate]);

  return (
    <div className={`onboarding-step${isWebView ? ' onboarding-step--webview' : ''}`}>
      {!hideIcon && (
        <div className="onboarding-step__icon-wrapper">
          <ErrorIcon className="onboarding-step__icon onboarding-step__icon--error" />
        </div>
      )}
      <h2
        className="onboarding-step__header"
        dangerouslySetInnerHTML={{ __html: title }}
      />
      <p
        className="onboarding-step__description"
        dangerouslySetInnerHTML={{ __html: description }}
      />
      <div className={`onboarding-step__actions${isWebView ? ' onboarding-step__actions--webview' : ''}`}>
        <Button
          variant="contained"
          onClick={handleRetry}
        >
          {translation.onboarding?.selectSubscriber?.retry}
        </Button>
        <Button
          variant={isWebView ? 'outlined' : 'text'}
          onClick={handleBack}
        >
          {isWebView
            ? translation.onboarding?.selectSubscriber?.backToLoginApp
            : translation.onboarding?.selectSubscriber?.backToLogin}
        </Button>
      </div>
    </div>
  );
};
